import { createClient } from "@sanity/client";
import { createImageUrlBuilder } from "@sanity/image-url";

const projectId = import.meta.env.VITE_SANITY_PROJECT_ID ?? "t6bjvowk";
const dataset = import.meta.env.VITE_SANITY_DATASET ?? "production";

export const sanityClient = createClient({
  projectId,
  dataset,
  useCdn: true,
  apiVersion: "2024-01-01",
});

const builder = createImageUrlBuilder(sanityClient);

export function urlFor(source: { _ref?: string } | { asset?: { _ref?: string } } | null | undefined): ReturnType<typeof builder.image> {
  return builder.image(source as Parameters<typeof builder.image>[0]);
}

export interface SanityImageDimensions {
  width: number;
  height: number;
  aspectRatio: number;
}

export interface SanityImage {
  _type: "image";
  asset: {
    _ref?: string;
    _id?: string;
    metadata?: {
      dimensions: SanityImageDimensions;
    };
  };
  alt?: string;
}

export interface NewsListItem {
  _id: string;
  title: string;
  slug: { current: string };
  date: string;
  excerpt: string;
  image: SanityImage | null;
  author: NewsArticleAuthor | null;
  categories: NewsArticleCategory[];
}

export interface NewsArticleAuthor {
  name: string;
}

export interface NewsArticleCategory {
  name: string;
  slug: { current: string };
}

export interface PortableTextBlock {
  _type: string;
  _key: string;
  [key: string]: unknown;
}

export interface NewsArticle extends Omit<NewsListItem, "excerpt"> {
  content: PortableTextBlock[];
  author: NewsArticleAuthor | null;
  categories: NewsArticleCategory[];
  image: SanityImage | null;
}

export interface HomePageSection {
  _key: string;
  title: string;
  published: boolean;
  important: boolean;
  content: PortableTextBlock[];
}

export interface HomePage {
  _id: string;
  homePageSection?: HomePageSection[];
}

export interface Settings {
  backgroundImages?: SanityImage[];
}

export interface DownloadItem {
  name: string;
  url: string | null;
}

export interface DownloadCategoryWithItems {
  _id: string;
  name: string;
  downloads: DownloadItem[];
}

export const downloadsPageQuery = `*[_type == "downloadCategory" && visible == true] | order(name asc) {
  _id,
  name,
  "downloads": *[_type == "download" && references(^._id)] | order(name asc) {
    name,
    "url": file.asset->url
  }
}`;

export const homePageQuery = `
{
  "homePage": *[_type == "homePage"][0] {
    _id,
    homePageSection
  },
  "settings": *[_type == "settings"][0] {
    backgroundImages
  }
}
`;

const LIST_PROJECTION = `{
  _id,
  title,
  slug,
  date,
  "excerpt": pt::text(content)[0...160],
  image,
  author->{ name },
  categories[]->{ name, slug }
}`;

export const newsListQuery = `*[_type == "post"] | order(date desc) [$start...$end] ${LIST_PROJECTION}`;

export const newsCountQuery = `count(*[_type == "post"])`;

export const newsArticleQuery = `*[_type == "post" && slug.current == $slug][0] {
  _id,
  title,
  slug,
  date,
  image {
    ...,
    asset->{
      _id,
      metadata {
        dimensions
      }
    }
  },
  content,
  author->{ name },
  categories[]->{ name, slug }
}`;

export interface ContactCategory {
  _id: string;
  category: string;
  slug: string;
  email: string;
}

export const contactCategoriesQuery = `*[_type == "contactCategories"] | order(category asc) {
  _id,
  category,
  "slug": slug.current,
  email
}`;

export interface FaqItem {
  _key: string;
  question: string;
  /** Portable text blocks, or legacy plain string from before schema change */
  answer: PortableTextBlock[] | string;
  popular?: boolean;
}

export interface FaqCategoryWithItems {
  _id: string;
  name: string;
  faqs: FaqItem[];
}

export const faqsPageQuery = `*[_type == "faqCategory" && visible != false] | order(name asc) {
  _id,
  name,
  faqs
}`;

export interface GalleryPhoto extends SanityImage {
  _key: string;
  alt?: string;
  caption?: string;
}

export interface GalleryCategory {
  _key: string;
  title: string;
  photos: GalleryPhoto[];
}

export interface Gallery {
  _id: string;
  categories: GalleryCategory[];
}

export const galleryPageQuery = `*[_type == "gallery"][0] {
  _id,
  categories[] {
    _key,
    title,
    photos[] {
      _key,
      ...,
      alt,
      caption
    }
  }
}`;
